import { toast } from 'sonner'
import { API_ROUTES } from '@/common/constants'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
    type ListOrdersParams,
    cancelOrder,
    getCompanyBySecurityCode,
    getHistoricalPrice,
    getHoldingsHistory,
    getInstrument,
    getInstrumentPrices,
    getBundle,
    getRecommendedSecurities,
    getSecuritiesBuyOrderStats,
    getSecuritiesByTag,
    getSecuritiesByTags,
    getTradingAccount,
    getTrendingSecurities,
    listHoldings,
    listOrders,
    listUserPledges,
    placeBuyOrder,
    placeSellOrder,
    getBundles,
    getEtfsByTags,
} from './tradingApi'
import { HistoricalPricesRangePreset, Maybe, OrderPayload, TradingAccountDetails } from '@/common/types'
import { type QueryOptions, defaultOptions } from '@/common/utils/queryOptions'

type ListOrdersQueryOptions = QueryOptions & ListOrdersParams

export const useFetchEquityDetails = (securityCode: Maybe<string>) => {
    return useQuery({
        queryKey: [API_ROUTES.instrumentProfile, securityCode],
        queryFn: () => getInstrument(securityCode || ''),
        enabled: !!securityCode,
    })
}

export const useFetchEquityPrices = (securityCode: Maybe<string>) => {
    return useQuery({
        queryKey: [API_ROUTES.instrumentPrices, securityCode],
        queryFn: () => getInstrumentPrices(securityCode || ''),
        enabled: !!securityCode,
    })
}

export const useFetchHistoricalPrice = (securityCode: Maybe<string>, rangePreset: HistoricalPricesRangePreset) => {
    return useQuery({
        queryKey: [API_ROUTES.historicalPrices, securityCode, rangePreset],
        queryFn: () => getHistoricalPrice(securityCode, rangePreset),
        enabled: !!securityCode,
    })
}

export const useFetchBundle = (bundleId: Maybe<string>) => {
    return useQuery({
        queryKey: [API_ROUTES.bundle, bundleId],
        queryFn: () => getBundle(bundleId || ''),
        enabled: !!bundleId,
    })
}

export const useFetchBundles = () => {
    return useQuery({
        queryKey: [API_ROUTES.bundles],
        queryFn: () => getBundles(),
        enabled: true,
    })
}

export const useFetchTradingAccount = (
    accountId: string | null,
    options: QueryOptions<TradingAccountDetails> = defaultOptions,
) => {
    const { enabled, refetchInterval } = options

    return useQuery<TradingAccountDetails | undefined>({
        queryKey: [API_ROUTES.tradingAccount, accountId],
        queryFn: getTradingAccount,
        enabled,
        refetchInterval: refetchInterval as any,
    })
}

export const useListHoldings = (accountId: string | null, options = defaultOptions) => {
    const { enabled, refetchInterval } = options
    return useQuery({
        queryKey: [API_ROUTES.listHoldings, accountId],
        queryFn: listHoldings,
        enabled,
        refetchInterval,
    })
}

export const useListUserPledges = () => {
    return useQuery({
        queryKey: [API_ROUTES.campaignUserPledgeList],
        queryFn: listUserPledges,
    })
}

export const useListOrders = (accountId: string | null, options: ListOrdersQueryOptions = defaultOptions) => {
    const { enabled, refetchInterval, ...params } = options
    return useQuery({
        queryKey: [API_ROUTES.listOrders, accountId],
        queryFn: () => listOrders(params),
        enabled,
        refetchInterval,
    })
}

type PlaceOrderVariables = OrderPayload & { accountId: string }

export const usePlaceBuyOrder = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({ accountId, ...order }: PlaceOrderVariables) => {
            void accountId
            return placeBuyOrder(order)
        },
        onSuccess: async (_data, variables) => {
            await queryClient.invalidateQueries({
                queryKey: [API_ROUTES.listOrders, variables.accountId],
            })
        },
        onError: () => {
            toast.error('Failed to place the order. Please try again')
        },
    })
}

export const usePlaceSellOrder = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({ accountId, ...order }: PlaceOrderVariables) => {
            void accountId
            return placeSellOrder(order)
        },
        onSuccess: async (_data, variables) => {
            await queryClient.invalidateQueries({
                queryKey: [API_ROUTES.listOrders, variables.accountId],
            })
        },
        onError: () => {
            toast.error('Failed to place the order. Please try again')
        },
    })
}

export const useCancelOrder = () => {
    return useMutation({
        mutationFn: (orderId: string) => cancelOrder(orderId),
        onSuccess: () => {},
        onError: () => {
            toast.error('Failed to cancel the order. Please try again')
        },
    })
}

export const useFetchRecommendedSecurities = () => {
    return useQuery({
        queryKey: [API_ROUTES.recommendedSecurities],
        queryFn: getRecommendedSecurities,
        enabled: true,
    })
}

export const useFetchHoldingsHistory = (accountId: string | null, range: string) => {
    return useQuery<{ endOfTradingDate: string; totalValue: number }[]>({
        queryKey: [API_ROUTES.getHoldingsHistory, accountId, range],
        queryFn: () => getHoldingsHistory(range),
        enabled: true,
    })
}

export const useFetchTrendingSecurities = () => {
    return useQuery({
        queryKey: [API_ROUTES.trendingSecurities],
        queryFn: () => getTrendingSecurities(),
        enabled: true,
    })
}

export const useFetchSecuritiesByTag = (tag: string) => {
    return useQuery({
        queryKey: [API_ROUTES.securitiesByTag, tag],
        queryFn: () => getSecuritiesByTag(tag),
        enabled: true,
    })
}

export const useFetchSecuritiesByTags = (tags: string[], enabled?: boolean) => {
    return useQuery({
        queryKey: [API_ROUTES.securitiesByTags, tags],
        queryFn: () => getSecuritiesByTags(tags),
        enabled: (enabled ?? true) && tags.length > 0,
    })
}

export const useFetchEtfsByTags = (positive?: string[], avoided?: string[], page: number = 1, enabled?: boolean) => {
    return useQuery({
        queryKey: [API_ROUTES.etfsByTags, positive, avoided, page],
        queryFn: () => getEtfsByTags(positive, avoided, page),
        enabled: (enabled ?? true) && ((positive?.length ?? 0) > 0 || (avoided?.length ?? 0) > 0),
    })
}

export const useFetchTopTradedSecurities = () => {
    const { data } = useQuery({
        queryKey: [API_ROUTES.securitiesByOrderStats],
        queryFn: () => getSecuritiesBuyOrderStats(),
        enabled: true,
    })

    const topTradedSecurities = data?.slice(0, 5) ?? []

    return { data: topTradedSecurities }
}

export const useFetchCompanyBySecurityCode = (securityCode: string) => {
    return useQuery({
        queryKey: [API_ROUTES.getCompanyByCode, securityCode],
        queryFn: () => getCompanyBySecurityCode(securityCode),
        enabled: true,
    })
}
